
'use client';

import { useState, useEffect, useCallback, useRef } from 'react';
import { KbvPluginService } from '@/services/kbvPluginService';
import { BANG_KEO_PAGE_LIMIT } from '@/contants';

export default function useBangKeoMatches(queryObject = {}) {
  const [hotLeaguesWithMatches, setHotLeaguesWithMatches] = useState([]);
  const [leaguesWithMatches, setLeaguesWithMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(queryObject.page || 1);
  const [limit] = useState(queryObject.limit || BANG_KEO_PAGE_LIMIT);
  const [total, setTotal] = useState(0);
  const loadMoreRef = useRef(null);
  const isLoadingMoreRef = useRef(false);

  const hasMore = page * limit < total;

  useEffect(() => {
    const fetchMatches = async () => {
      try {
        setLoading(true);
        setError(null);
        const result = await KbvPluginService.fetchBangKeoMatches(queryObject);
        setLeaguesWithMatches(result?.data?.data || []);

        result?.total && setTotal(result.total);
        result?.page && setPage(result.page);
      } catch (err) {
        setError(err);
        setLeaguesWithMatches([]);
      } finally {
        setLoading(false);
      }
    };

    fetchMatches();
  }, [JSON.stringify(queryObject)]);

  useEffect(() => {
    const fetchHotLeagues = async () => {
      const result = await KbvPluginService.fetchBangKeoMatches({
        include: "homeTeam,awayTeam,odd,countOtherOdds,stats,events,insight",
        filterInclude: `date:all`,
        filterOdds: -1,
        cache: false,
        status,
        page: 1,
        limit: BANG_KEO_PAGE_LIMIT,

      });
      setHotLeaguesWithMatches(result?.data?.data || []);
    };
    fetchHotLeagues();
  }, []);



  const handleLoadMore = useCallback(async () => {
      if (isLoadingMoreRef.current || !hasMore) return;

      isLoadingMoreRef.current = true;
      try {
        const nextPage = page + 1;
        const result = await KbvPluginService.fetchBangKeoMatches({
          ...queryObject,
          page: nextPage,
          limit,
        });

        let newItems = result?.data?.data || [];

        if (newItems.length) {
          setLeaguesWithMatches((prev) => [...prev, ...newItems]);
          setPage(nextPage);
        }
      } catch (err) {
        console.error('Load more error:', err);
      } finally {
        isLoadingMoreRef.current = false;
      }
    }, [page, limit, queryObject, hasMore]);

  useEffect(() => {
    if (!hasMore || !loadMoreRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !isLoadingMoreRef.current) {
          handleLoadMore();
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(loadMoreRef.current);
    return () => {
      if (loadMoreRef.current) {
        observer.unobserve(loadMoreRef.current);
      }
    };
  }, [hasMore, handleLoadMore]);

  return {
    hotLeaguesWithMatches,
    leaguesWithMatches,
    loading,
    error,
    hasMore,
    loadMoreRef,
    isLoadingMoreRef,
    matches: hotLeaguesWithMatches.flatMap(({ matches }) => matches),
  };
}